/**
 * PremiumBadge — Visual indicator for Pro instructors.
 *
 * Two variants as seen in the Figma mockups:
 *
 *   'pill'    — solid red pill with star icon + "PRO" text.
 *               Used on: profile page (beside name), Settings row sublabel,
 *               success screen, /settings/subscription hero.
 *
 *   'outlined' — outlined badge with star icon + "PREMIUM" text.
 *                Used on: InstructorBrowseCard (Find Instructors list).
 *
 * Size variants:
 *   'sm' — default, used inline in lists
 *   'xs' — tight inline (beside name in text flows)
 *
 * Usage:
 *   <PremiumBadge />                   // pill sm (profile page)
 *   <PremiumBadge variant="outlined" /> // outlined sm (browse card)
 *   <PremiumBadge size="xs" />          // small pill (beside name)
 */

import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PremiumBadgeProps {
  variant?: 'pill' | 'outlined';
  size?: 'xs' | 'sm';
  className?: string;
}

export const PremiumBadge: React.FC<PremiumBadgeProps> = ({
  variant = 'pill',
  size = 'sm',
  className,
}) => {
  const iconSize = size === 'xs' ? 8 : 10;
  const label    = variant === 'outlined' ? 'PREMIUM' : 'PRO';

  if (variant === 'outlined') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full border font-semibold tracking-wide',
          'border-[#D72E16] text-[#D72E16]',
          size === 'xs'
            ? 'px-1.5 py-0.5 text-[9px]'
            : 'px-2 py-0.5 text-[10px]',
          className
        )}
        aria-label="Premium instructor"
      >
        <Star size={iconSize} fill="currentColor" strokeWidth={0} />
        {label}
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full font-semibold tracking-wide',
        'bg-[#D72E16] text-white',
        size === 'xs'
          ? 'px-1.5 py-0.5 text-[9px]'
          : 'px-2 py-0.5 text-[10px]',
        className
      )}
      aria-label="Pro instructor"
    >
      <Star size={iconSize} fill="currentColor" strokeWidth={0} />
      {label}
    </span>
  );
};
